"use client";

import { useAppSelector } from "@/store/hooks";
import { fetchBanner, TopsortBannerConfig, WinnerResult } from "@/utils/topsortUtil";
import { useEffect, useMemo, useRef, useState } from "react";

import HeaderBanner from "@/app/components/banners/headerBanner";
import { SearchTypes } from "@/declarations/search";
import { useWindowWidth } from "@react-hook/window-size";
import { SearchResponse } from "instantsearch.js";

export default function HeaderBanners({ searchResult, topsortBannerConfig }: HeaderBannersProps) {
  const topsortJsApiKey = useAppSelector((state) => state.config.topsortJsApiKey);
  const [topsortWinnerType, setTopsortWinnerType] = useState<WinnerResult | null>(null);

  const windowWidth = useWindowWidth();

  const topsortBannerType = useMemo(() => {
    return windowWidth < 576 ? "mobile" : "desktop";
  }, [windowWidth]);

  const topsortHitsHash = useMemo(() => {
    if (!topsortBannerConfig || !searchResult?.hits || searchResult.hits.length === 0) {
      return null;
    }
    const hits = searchResult.hits as SearchTypes.ProductHit[];
    return hits.map((hit) => hit.mp_id).join("|");
  }, [topsortBannerConfig, searchResult?.hits]);

  useEffect(() => {
    setTopsortWinnerType(null);
  }, [topsortBannerType]);

  const lastFetchedHashRef = useRef<string | null>(null);
  const lastBannerTypeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!topsortBannerConfig || !topsortHitsHash) return;

    const isNewHash = topsortHitsHash !== lastFetchedHashRef.current;
    const bannerTypeChanged = topsortBannerType !== lastBannerTypeRef.current;

    if (isNewHash || bannerTypeChanged) {
      lastFetchedHashRef.current = topsortHitsHash;
      lastBannerTypeRef.current = topsortBannerType;

      fetchBanner(
        topsortBannerConfig.auctionType,
        topsortBannerConfig.header,
        topsortJsApiKey,
        topsortBannerType,
        topsortBannerConfig.filter,
      )
        .then((winnerData) => {
          setTopsortWinnerType(winnerData);
        })
        .catch(console.error)
        .finally(() => {});
    }
  }, [topsortBannerConfig, topsortHitsHash, topsortBannerType, topsortJsApiKey]);

  const topsortWinner = useMemo(() => {
    if (topsortWinnerType && topsortWinnerType.found) {
      return topsortWinnerType.winner;
    }
    return null;
  }, [topsortWinnerType]);

  return <HeaderBanner topsortWinner={topsortWinner} topsortBannerType={topsortBannerType} />;
}

export type HeaderBannersProps = {
  searchResult: SearchResponse<SearchTypes.ProductHit>;
  topsortBannerConfig: TopsortBannerConfig;
};
